import * as React from "react";
import { Calendar } from "lucide-react";

import { getFirstFqlTicker } from "../../ai/tableHelpers";
import type { DocumentSourceItem, SourceItem } from "../../ai/sourceItems";
import { getCitationColor, getCitationLabel } from "../colors";
import {
  formatSourceDate,
  getTableDisplayTitle,
  getTableEntityDisplay,
  isStreetAccountSource,
  shouldShowTableHeaderTitle,
} from "./sourceDisplay";

const FACTSET_LOGO_SRC = "/assets/factset-logo.svg";

export const SourceHeader: React.FC<{ item: SourceItem }> = ({ item }) => {
  const dataSource =
    item.kind === "document"
      ? (item.link.metadata?.dataSource ?? item.envelope.metadata?.dataSource)
      : item.metadata?.dataSource;
  const label = getCitationLabel(dataSource, item.client);
  const color = getCitationColor(
    dataSource ?? (item.kind === "table" ? item.label ?? item.client : item.client)
  );

  if (item.kind === "document") {
    const meta = item.link.metadata;
    if (isStreetAccountSource(dataSource, item.client)) {
      return <StreetAccountHeader item={item} label={label} color={color} />;
    }
    const title = meta?.documentName?.trim() || item.link.link || "Untitled source";
    return (
      <header className="flex shrink-0 flex-col gap-2 border-b border-border px-1 pb-4">
        <SourceHeaderLabel
          label={label}
          color={color}
          detail={meta?.documentCategory}
        />
        <h1 className="m-0 max-w-[760px] text-[18px] font-semibold leading-snug tracking-tight text-foreground">
          {title}
        </h1>
        {(meta?.companyName || meta?.datePublished) && (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-muted-foreground">
            {meta?.companyName && <span>{meta.companyName}</span>}
            {meta?.datePublished && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" aria-hidden />
                {formatSourceDate(meta.datePublished)}
              </span>
            )}
          </p>
        )}
      </header>
    );
  }

  const meta = item.metadata as
    | { ticker?: string; documentName?: string; datePublished?: string }
    | undefined;
  const rawTicker = meta?.ticker ?? getFirstFqlTicker(item);
  const entityDisplay = getTableEntityDisplay(item);
  const ticker = entityDisplay.hasMultipleEntities ? undefined : rawTicker;
  const title =
    entityDisplay.summary && entityDisplay.summary !== rawTicker
      ? entityDisplay.summary
      : getTableDisplayTitle(item, label, rawTicker);
  const showTitle =
    entityDisplay.summary && entityDisplay.summary !== rawTicker
      ? true
      : shouldShowTableHeaderTitle(title, label, rawTicker);
  return (
    <header className="flex shrink-0 flex-col gap-2 border-b border-border px-1 pb-4">
      <SourceHeaderLabel label={label} color={color} />
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h1 className="m-0 text-[22px] font-semibold leading-none tracking-tight text-foreground">
          Data powered by
        </h1>
        <FactSetLogo />
      </div>
      {(showTitle || ticker) && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {showTitle && (
            <h2 className="m-0 max-w-[760px] text-[15px] font-semibold leading-snug text-foreground">
              {title}
            </h2>
          )}
          {ticker && (
            <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-mono font-semibold text-muted-foreground">
              {ticker}
            </span>
          )}
        </div>
      )}
      {meta?.datePublished && (
        <p className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <Calendar className="h-3 w-3" aria-hidden />
          {formatSourceDate(meta.datePublished)}
        </p>
      )}
    </header>
  );
};

const StreetAccountHeader: React.FC<{
  item: DocumentSourceItem;
  label: string;
  color: string;
}> = ({ item, label, color }) => {
  const meta = item.link.metadata;
  const date = formatSourceDate(meta?.datePublished);
  const title = meta?.documentName?.trim() || item.link.link || "Untitled source";

  return (
    <header className="flex shrink-0 flex-col gap-2 border-b border-border px-1 pb-4">
      <SourceHeaderLabel label={label} color={color} />
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h1 className="m-0 text-[22px] font-semibold leading-none tracking-tight text-foreground">
          Data powered by
        </h1>
        <FactSetLogo />
      </div>
      {date && <p className="text-[12px] text-muted-foreground">{date}</p>}
      <h2 className="m-0 max-w-[760px] pt-2 text-[16px] font-semibold italic leading-snug text-foreground">
        {title}
      </h2>
    </header>
  );
};

const SourceHeaderLabel: React.FC<{
  label: string;
  color: string;
  detail?: string;
}> = ({ label, color, detail }) => (
  <p className="inline-flex w-fit items-center gap-1.5 text-[12px] font-medium text-muted-foreground">
    <span
      className="h-1.5 w-1.5 rounded-full"
      style={{ backgroundColor: color }}
      aria-hidden
    />
    <span>{label}</span>
    {detail && <span className="text-muted-foreground/70">/ {detail}</span>}
  </p>
);

const FactSetLogo: React.FC = () => (
  <img
    src={FACTSET_LOGO_SRC}
    alt="FactSet"
    className="h-[30px] w-auto translate-y-[2px]"
  />
);
